import { pgSelect, pgEq } from './postgrest';

interface AgCapDescriptor {
  class: string;
  percent: number;
  modifier: string;
}

export class AcronymConversionService {
  letterToTexture(letter: string): string {
    const textureMap: { [key: string]: string } = {
      'C': 'Clay',
      'CL': 'Clay loam',
      'CS': 'Coarse sand',
      'FCL': 'Fine sandy clay loam',
      'FL': 'Fine sandy loam',
      'FS': 'Fine sand',
      'HC': 'Heavy clay',
      'L': 'Loam',
      'O': 'Organic',
      'S': 'Sand',
      'SCL': 'Sandy clay loam',
      'SIC': 'Silty clay',
      'SICL': 'Silty clay loam',
      'SIL': 'Silty loam',
      'SL': 'Sandy loam',
      'VL': 'Very fine sandy loam',
      'GS': 'Gravelly sand',
      'LS': 'Loamy sand',
      'GLS': 'Gravelly loamy sand',
      'LFS': 'Loamy fine sand',
      'GSL': 'Gravelly sandy loam',
      'VCL': 'Very fine sandy clay loam',
      'GL': 'Gravelly loam',
      'U': 'Unclassified',
    };
    return textureMap[letter] || letter;
  }

  async salityClassToDescription(code: string): Promise<string[]> {
    try {
      const result = await pgSelect('salsym', pgEq('salsym', code));
      return result.resource.map((item: any) => item.affect_on_prod);
    } catch (error) {
      console.error('Error fetching salinity description:', error);
      throw error;
    }
  }

  async agCapabilityToDescription(capability: string): Promise<string[]> {
    try {
      const digit = capability.charAt(0);
      const result = await pgSelect('capclass', pgEq('domi_capablity_class', digit));
      return result.resource.map((item: any) => item.limitation);
    } catch (error) {
      console.error('Error fetching ag capability description:', error);
      throw error;
    }
  }

  agCapabilityToBreakdown(capability: string): AgCapDescriptor[] {
    const caps: AgCapDescriptor[] = [];
    const a = capability.slice(0, 7);
    const b = capability.slice(7, 13);
    const c = capability.slice(13, 19);
    const r = [a, b, c];

    r.forEach((x, i) => {
      if (i === 0) {
        const classChar = x.charAt(0);
        let percent;
        if (x.charAt(2) === '1') {
          percent = 100;
        } else {
          percent = 10 * parseInt(x.charAt(3));
        }
        const mods = x.charAt(5) + x.charAt(6);
        caps.push({ class: classChar, percent, modifier: mods });
      } else {
        const classChar = x.charAt(0);
        const percent = 10 * parseInt(x.charAt(2));
        const mods = x.charAt(4) + x.charAt(5);
        caps.push({ class: classChar, percent, modifier: mods });
      }
    });
    return caps.filter((a) => !isNaN(a.percent));
  }

  agCapModToText(mod: string): string {
    const modChars = mod.split('');
    let modStrings = '';
    const dict: { [key: string]: string } = {
      'C': 'Adverse climate',
      'D': 'Undesirable soils structure and/or low permeability',
      'E': 'Erosion',
      'F': 'Low fertility',
      'I': 'Inundation by streams or lakes',
      'M': 'Moisture limitations',
      'N': 'Salinity',
      'P': 'Stoniness',
      'R': 'Consolidated bedrock',
      'S': 'Combination of subclasses',
      'T': 'Topography',
      'W': 'Excess water',
      'X': 'Cumulative effect of two or more adverse characteristics',
    };

    modChars.forEach((m) => {
      if (m !== ' ') {
        modStrings += dict[m];
        modStrings += '. ';
      }
    });

    return modStrings ? modStrings : 'No modifier';
  }

  async phToDefinition(ph: string): Promise<string[]> {
    try {
      const result = await pgSelect('ph_symbol_definitions', pgEq('symbol', ph));
      return result.resource.map((item: any) => item.definition);
    } catch (error) {
      console.error('Error fetching pH definition:', error);
      throw error;
    }
  }

  async polygonLabelToAssociationDescription(label: string): Promise<string[]> {
    try {
      // `Assoc`/`Assoc_description` are mixed-case columns — PostgREST is case-sensitive.
      const result = await pgSelect('pm', pgEq('Assoc', label));
      return result.resource.map((item: any) => item.Assoc_description.replace(/�/g, ''));
    } catch (error) {
      console.error('Error fetching association description:', error);
      throw error;
    }
  }

  async mapunitToDescription(mu: string): Promise<string[]> {
    try {
      // `Mapunit_and_Modifier`/`Soil_Landscape` are mixed-case columns.
      const result = await pgSelect(
        'soiland',
        pgEq('Mapunit_and_Modifier', mu.substring(0, mu.indexOf(':'))),
      );
      return result.resource.map((item: any) => item.Soil_Landscape);
    } catch (error) {
      console.error('Error fetching mapunit description:', error);
      throw error;
    }
  }
}

export const acronymService = new AcronymConversionService();
